import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { styled, alpha } from '@mui/material/styles';
import { useMyBookings, useCancelBooking } from '../hooks/useBookings';
import { BookingCard } from '../components/BookingCard';
import { CancellationModal } from '../components/CancellationModal';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { Alert } from '../../../components/ui/Alert';
import { EmptyState } from '../../../components/ui/EmptyState';
import { FilterTabs } from '../../../components/ui/FilterTabs';
import type { BookingStatusValue } from '@lov/shared';
import { parseError } from '@lov/shared';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Bookings' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'NO_SHOW', label: 'No Show' },
];

const PageHeader = styled(Box)({
  marginBottom: 32,
});

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.75rem',
  color: theme.palette.text.primary,
  marginBottom: 8,
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
}));

const BookingsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  marginTop: 32,
});

const NavButton = styled('button')(({ theme }) => ({
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
  '&:hover:not(:disabled)': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export function MyBookingsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<BookingStatusValue | ''>('');
  const [page, setPage] = useState(1);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const { data, isLoading, error } = useMyBookings({
    page,
    limit: 10,
    status: statusFilter || undefined,
  });

  const cancelMutation = useCancelBooking();

  const handleCancelClick = (bookingId: string) => {
    setCancellingId(bookingId);
  };

  const handleCancelConfirm = async (bookingId: string, reason: string) => {
    try {
      await cancelMutation.mutateAsync({
        id: bookingId,
        reason: reason || undefined,
      });
      setCancellingId(null);
    } catch (error) {
      console.error('Cancel failed:', error);
    }
  };

  const handleCloseDialog = () => {
    setCancellingId(null);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <PageHeader>
          <PageTitle>Bookings</PageTitle>
          <PageSubtitle>View and manage your booking history</PageSubtitle>
        </PageHeader>

        {/* Filters */}
        <Box sx={{ mb: 4 }}>
          <FilterTabs
            tabs={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as BookingStatusValue | '');
              setPage(1);
            }}
          />
        </Box>

        {/* Content */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <LoadingSpinner size="lg" />
          </Box>
        ) : error ? (
          <Alert variant="error" title="Failed to load bookings">
            {parseError(error).message}
          </Alert>
        ) : data?.data && data.data.length > 0 ? (
          <>
            {/* Bookings List */}
            <BookingsList>
              {data.data.map((booking) => (
                <BookingCard key={booking.id} booking={booking} onCancel={handleCancelClick} />
              ))}
            </BookingsList>

            {/* Pagination */}
            {data.meta?.pagination && data.meta.pagination.totalPages > 1 && (
              <PaginationContainer>
                <NavButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  Previous
                </NavButton>
                <Typography
                  sx={{
                    px: 2,
                    py: 1,
                    color: 'text.secondary',
                    fontFamily: '"IBM Plex Sans", sans-serif',
                    fontSize: '0.875rem',
                  }}
                >
                  Page {page} of {data.meta.pagination.totalPages}
                </Typography>
                <NavButton
                  onClick={() => setPage((p) => Math.min(data.meta.pagination.totalPages, p + 1))}
                  disabled={page === data.meta.pagination.totalPages}
                >
                  Next
                </NavButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <EmptyState
            variant="bookings"
            description={
              statusFilter
                ? `You don't have any ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label.toLowerCase()} bookings.`
                : "No bookings yet."
            }
            actionLabel="Browse Venues"
            onAction={() => navigate('/venues')}
          />
        )}
      </Container>

      <CancellationModal
        bookingId={cancellingId}
        variant="customer"
        onClose={handleCloseDialog}
        onConfirm={handleCancelConfirm}
        isSubmitting={cancelMutation.isPending}
      />
    </Box>
  );
}
